import React, { useEffect, useState } from "react";
import "../Dashboard/Dashboard.scss";
import LineChartIcon from "../../../assets/images/vactor/line-chart-icon.svg";
import BarChartIcon from "../../../assets/images/vactor/bar-chart-icon.svg";
import BarChart from "../../../components/AdminComponents/ApexChart/Barchart";
import AreaChart from "../../../components/AdminComponents/ApexChart/AreaChart";
import { useAppDispatch, useAppSelector } from "../../../Store/store";
import { getUserStats, getUserGrowth, getSubscriptionStats } from "../../../Services/admin.service";
import { format, subDays, subMonths, subYears, endOfWeek, endOfMonth, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval } from "date-fns";
import { getUserDetails } from "../../../Services/profile.service";
import { colors, styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";

const TIME_RANGE_OPTIONS = ['5D', '2W', '1M', '6M', '1Y'] as const;
type TimeRange = typeof TIME_RANGE_OPTIONS[number];

interface SubscriptionBucket {
  month?: string;
  bucket_start?: string;
  onboarded?: number;
  subscriptions?: number;
  free_users?: number;
}

interface SubscriptionFilters {
  days?: number;
  group_by?: 'day' | 'week' | 'month';
}

// sidebar tooltip 

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];
const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#fff',
    '&::before': {
      backgroundColor: '#fff',
      border: '1px solid #E2E4E9',
    },
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff', 
    boxShadow: '0px 12px 24px 0px rgba(134, 140, 152, 0.12)',
    fontSize: '12px',
    color:"#0A0D14",
    fontWeight: 400,
    height: '30px',
    border: '1px solid #E2E4E9',
    borderRadius: '6px',
    textAlign: 'center',
    padding: '4px 10px',
    fontFamily: '"Inter", "sans-serif"',
  },
}));
const Dashboard = () => {
  const [userStats, setUserStats] = useState<any | null>(null);
  const [currentTimeRange, setCurrentTimeRange] = useState<TimeRange>('5D');
  const [subscriptionTimeRange, setSubscriptionTimeRange] = useState<TimeRange>('5D');
  const [chartData, setChartData] = useState<{ data: number[], categories: string[] }>({ data: [], categories: [] });
  const [subscriptionChartData, setSubscriptionChartData] = useState<{
    data: { name: string; data: number[] }[];
    categories: string[];
  }>({ data: [], categories: [] });
  const dispatch = useAppDispatch();

  const user=useAppSelector(state => state.user.user);

  useEffect(() => {
  const fetchUserDetails =  () => {
     dispatch(getUserDetails(localStorage.getItem("id")));
  };
  fetchUserDetails();
}, []);

const processDataForChart = (subscriptions: any[]) => {
  const months = subscriptions.map((item: any) => item.month); // ['Jan', 'Feb', 'Mar', ...]
  const onboardedData = subscriptions.map((item: any) => item.onboarded);
  const subscriptionsData = subscriptions.map((item: any) => item.subscriptions);
  const freeUsersData = subscriptions.map((item: any) => item.free_users);

  return {
    data: [
      { name: 'Onboarded', data: onboardedData },
      { name: 'Subscriptions', data: subscriptionsData },
      { name: 'Free Users', data: freeUsersData }
    ],
    categories: months
  };
};
useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await dispatch(getUserStats({})).unwrap();
      if (res.status) {
        // Process the data received from the backend
        const processedData = processDataForChart(res.subscriptions); // Assume `subscriptions` is the array from backend
        setChartData({
          data: processedData.data.length > 0 ? processedData.data[0].data : [],
          categories: processedData.categories
        }); // Set the processed data for the chart
      }
    } catch (err) {
      console.error("Error fetching data", err);
    }
  };

  fetchData();
}, [dispatch]);



  function fillMissingDatesForChart(
    startDateStr: string,
    endDateStr: string,
    data: { date: string; count: number }[]
  ) {
    const result = [];
    const map = new Map(data.map(item => [item.date, item.count]));

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const isoDate = d.toISOString().split("T")[0];
      result.push({
        label: isoDate,
        count: map.get(isoDate) || 0,
      });
    }

    return result;
  }

  // Group data by weeks
  function groupDataByWeeks(data: { date: string; count: number }[], startDate: Date, endDate: Date) {
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 }); // Monday start
    
    return weeks.map(weekStart => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const weekData = data.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= weekStart && itemDate <= weekEnd;
      });
      
      const totalCount = weekData.reduce((sum, item) => sum + item.count, 0);

      return {
        label: `Week of ${format(weekStart, 'dd MMM')}`,
        count: totalCount
      };
    });
  }

  // Group data by months
  function groupDataByMonths(data: { date: string; count: number }[], startDate: Date, endDate: Date) {
    const months = eachMonthOfInterval({ start: startDate, end: endDate });
    
    return months.map(monthStart => {
      const monthEnd = endOfMonth(monthStart);
      const monthData = data.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= monthStart && itemDate <= monthEnd;
      });
      
      const totalCount = monthData.reduce((sum, item) => sum + item.count, 0);
      
      return {
        label: format(monthStart, 'MMM yyyy'),
        count: totalCount
      };
    });
  }

  // Fetch data for specific time range
  const fetchDataForTimeRange = async (timeRange: TimeRange) => {
    const endDate = new Date();
    let startDate: Date;
    
    switch (timeRange) {
      case '5D':
        startDate = subDays(endDate, 4); // Last 5 days including today
        break;
      case '2W':
        startDate = subDays(endDate, 13); // Last 14 days
        break;
      case '1M':
        startDate = subDays(endDate, 30); // Last 31 days including today
        break;
      case '6M':
        startDate = subDays(endDate, 180); // Last 181 days including today
        break;
      case '1Y':
        startDate = subYears(endDate, 1); // Same date last year
        break;
      default:
        startDate = subDays(endDate, 4);
    }

    const formattedStart = format(startDate, "yyyy-MM-dd");
    const formattedEnd = format(endDate, "yyyy-MM-dd");

    try {
      // Fetch User Growth specifically for the graph
      const growthRes = await dispatch(
        getUserGrowth({
          start_date: formattedStart,
          end_date: formattedEnd,
        })
      ).unwrap();
      // Process data based on time range
      let processedData;
      const rawGrowthData = growthRes.user_growth || growthRes || [];
      if (timeRange === '5D' || timeRange === '2W') {
        // Daily data
        processedData = fillMissingDatesForChart(
          formattedStart,
          formattedEnd,
          rawGrowthData
        );
        // Format dates for better display
        processedData = processedData.map(item => ({
          ...item,
          label: timeRange === '5D' ? format(new Date(item.label), 'EEE') : format(new Date(item.label), 'MMM dd')
        }));
      } else if (timeRange === '1M') {
        // Weekly data for 1 month
        processedData = groupDataByWeeks(rawGrowthData, startDate, endDate);
      } else if (timeRange === '6M' || timeRange === '1Y') {
        // Monthly data for 6M and 1Y
        processedData = groupDataByMonths(rawGrowthData, startDate, endDate);
      }

      setChartData({
        data: processedData?.map(item => item.count) || [],
        categories: processedData?.map(item => item.label) || []
      });

    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchSubscriptionData = async (timeRange: TimeRange) => {
    const subscriptionDaysByRange: Record<TimeRange, number> = {
      '5D': 5,
      '2W': 14,
      '1M': 30,
      '6M': 180,
      '1Y': 365,
    };

    try {
      const res = await dispatch(
        getSubscriptionStats({ subscription_days: subscriptionDaysByRange[timeRange] })
      ).unwrap();
      const rawData: SubscriptionBucket[] = res.subscriptions || res || [];
      const filters: SubscriptionFilters = res.subscription_filters || {};
      const categories = rawData.length > 0
        ? rawData.map((item) => formatSubscriptionCategory(item, filters.group_by, timeRange))
        : getSubscriptionCategories(timeRange);

      const onboardedData = rawData.length > 0
        ? rawData.map((item) => Number(item.onboarded ?? 0))
        : categories.map(() => 0);
      const subscriptionData = rawData.length > 0
        ? rawData.map((item) => Number(item.subscriptions ?? 0))
        : categories.map(() => 0);
      const freeUsersData = rawData.length > 0
        ? rawData.map((item) => Number(item.free_users ?? 0))
        : categories.map(() => 0);

      setSubscriptionChartData({
        categories,
        data: [
          { name: "Onboarded", data: onboardedData },
          { name: "Subscriptions", data: subscriptionData },
          { name: "Free User", data: freeUsersData },
        ],
      });
    } catch (err) {
      console.error("Error fetching subscription data:", err);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (timeRange: TimeRange) => {
    setCurrentTimeRange(timeRange);
    fetchDataForTimeRange(timeRange);
  };

  const fetchGeneralStats = async () => {
    try {
      const statsRes = await dispatch(getUserStats()).unwrap();
      setUserStats(statsRes);
    } catch (err) {
      console.error("Error fetching general stats:", err);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchGeneralStats();
    fetchDataForTimeRange('5D');
    fetchSubscriptionData('1M');
  }, []);

  // Prepare data for charts
  const columndata = chartData.data.length > 0
    ? [{ name: "Onboarded", data: chartData.data }]
    : [];

  const columncategories = chartData.categories;

  const getSubscriptionCategories = (timeRange: TimeRange) => {
    const today = new Date();

    switch (timeRange) {
      case '5D':
        return eachDayOfInterval({ start: subDays(today, 4), end: today }).map((date) =>
          format(date, 'dd MMM')
        );
      case '2W':
        return eachWeekOfInterval(
          { start: subDays(today, 13), end: today },
          { weekStartsOn: 1 }
        ).map((date) => `Wk ${format(date, 'dd MMM')}`);
      case '1M':
        return eachWeekOfInterval(
          { start: subMonths(today, 1), end: today },
          { weekStartsOn: 1 }
        ).map((date) => format(date, 'dd MMM'));
      case '6M':
        return eachMonthOfInterval({ start: subMonths(today, 5), end: today }).map((date) =>
          format(date, 'MMM')
        );
      case '1Y':
        return eachMonthOfInterval({ start: subMonths(today, 11), end: today }).map((date) =>
          format(date, 'MMM')
        );
      default:
        return [];
    }
  };

  const formatSubscriptionCategory = (
    item: SubscriptionBucket,
    groupBy?: SubscriptionFilters['group_by'],
    timeRange?: TimeRange
  ) => {
    if (item.bucket_start) {
      const bucketDate = new Date(item.bucket_start);

      if (groupBy === 'day') {
        return timeRange === '5D'
          ? format(bucketDate, 'dd MMM')
          : format(bucketDate, 'dd MMM');
      }

      if (groupBy === 'week') {
        return `Week of ${format(bucketDate, 'dd MMM')}`;
      }

      if (groupBy === 'month') {
        return format(bucketDate, 'MMM');
      }
    }

    return item.month || '';
  };

  const handleSubscriptionTimeRangeChange = (range: TimeRange) => {
    setSubscriptionTimeRange(range);
    fetchSubscriptionData(range);
  };

  return (
    <>
      <div className="admin-dashboard-wrap w-100">
        <div className="admin-dashboard-status-card-section">
          <div className="row gy-4">
            <div className="col-sm-6 col-md-4 col-lg-3">
              <div className="common-card-wrap">
                <div className="admin-status-card-head">
                  <div className="admin-status-card-head-label">All Users</div>
                  <BootstrapTooltip title="Last 24 hours">
                  <div className="admin-status-card-head-percentage">
                    {userStats?.total_users_growth}
                  </div>
                  </BootstrapTooltip>
                </div>
                <div className="admin-status-card-valuenum">
                  <div className="admin--card-status-number">{userStats?.total_users}</div>
                  <div className="admin--card-status-sublabel">All Time</div>
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-md-4 col-lg-3">
              <div className="common-card-wrap">
                <div className="admin-status-card-head">
                  <div className="admin-status-card-head-label">
                    New Signups
                  </div>
                  <BootstrapTooltip title="Last 7 days">
                  <div className="admin-status-card-head-percentage">{userStats?.signups_this_week_growth}</div>
                  </BootstrapTooltip>
                </div>
                <div className="admin-status-card-valuenum">
                  <div className="admin--card-status-number">{userStats?.signups_this_week}</div>
                  <div className="admin--card-status-sublabel">This Week</div>
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-md-4 col-lg-3">
              <div className="common-card-wrap">
                <div className="admin-status-card-head">
                  <div className="admin-status-card-head-label">Paid Users</div>
                  <BootstrapTooltip title="Last 7 Days">
                  <div className="admin-status-card-head-percentage">
                    {userStats?.paid_users_growth}
                  </div>
                  </BootstrapTooltip>
                </div>
                <div className="admin-status-card-valuenum">
                  <div className="admin--card-status-number">{userStats?.paid_users}</div>
                  <div className="admin--card-status-sublabel">Users</div>
                </div>
              </div>
            </div>

            <div className="col-sm-6 col-md-4 col-lg-3">
              <div className="common-card-wrap">
                <div className="admin-status-card-head">
                  <div className="admin-status-card-head-label">Free Users</div>
                  <BootstrapTooltip title="Last 7 Days">
                  <div className="admin-status-card-head-percentage">{userStats?.free_users_growth}</div>
                  </BootstrapTooltip>
                </div>
                <div className="admin-status-card-valuenum">
                  <div className="admin--card-status-number">{userStats?.free_users}</div>
                  <div className="admin--card-status-sublabel">Users</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Start Chart section  */}
        <div className="admin-dashboard-chart-section">
          <div className="row gy-4">
            <div className="col-md-6">
              <div className="common-card-wrap common-chart-card">
                <div className="common-chart-card-header">
                  <div className="chart-card-header-title">
                    <img src={LineChartIcon} alt="" />
                    User Growth
                  </div>
                </div>
                <div className="chart-body">
                   <AreaChart 
                     tasks={columndata} 
                     categories={columncategories} 
                     onTimeRangeChange={handleTimeRangeChange}
                     currentTimeRange={currentTimeRange}
                   />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="common-card-wrap common-chart-card">
                <div className="common-chart-card-header">
                  <div className="chart-card-header-title">
                    <img src={BarChartIcon} alt="" />
                    Subscriptions
                  </div>
                </div>
                <div className="chart-body">
                  <BarChart
                    tasks={subscriptionChartData.data}
                    categories={subscriptionChartData.categories}
                    onTimeRangeChange={handleSubscriptionTimeRangeChange}
                    currentTimeRange={subscriptionTimeRange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
